import { Link } from "react-router-dom";
import { MapPin, Mail, Phone } from "lucide-react";
import { HYDERABAD_ADDRESS, SERVICES, BRAND } from "@/lib/data";
import LogoWordmark from "@/components/LogoWordmark";

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer data-testid="site-footer" className="bg-brand-ink text-brand-bg pt-20 pb-10 mt-24">
            <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-4">
                    <div className="mb-6">
                        <LogoWordmark height={36} tone="light" />
                        <p className="overline text-brand-amber mt-4">Shared Solutions</p>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed max-w-sm">
                        A Hyderabad-based shared-services partner. We build your extended office from India —
                        bookkeeping, taxation, advisory and back-office talent, seamlessly integrated as part of your team.
                    </p>
                    <div className="ornament mt-6 max-w-[180px]" aria-hidden="true" />
                </div>

                <div className="md:col-span-2">
                    <p className="overline mb-4 text-brand-amber">Explore</p>
                    <ul className="space-y-2 text-sm text-white/80">
                        {[
                            ["/", "Home"], ["/about", "About"], ["/services", "Services"],
                            ["/industries", "Industries"], ["/pricing", "Pricing"],
                            ["/team", "Team"], ["/contact", "Contact"],
                        ].map(([to, label]) => (
                            <li key={to}>
                                <Link to={to} className="link-underline" data-testid={`footer-link-${label.toLowerCase()}`}>
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="overline mb-4 text-brand-amber">Services</p>
                    <ul className="space-y-2 text-sm text-white/80">
                        {SERVICES.india.map(s => (
                            <li key={s.slug}>
                                <Link to="/services" className="link-underline">{s.title}</Link>
                            </li>
                        ))}
                        <li className="pt-2 text-white/50 text-xs uppercase tracking-[0.18em]">International</li>
                        {SERVICES.international.slice(0, 3).map(s => (
                            <li key={s.slug}>
                                <Link to="/services" className="link-underline">{s.title}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="overline mb-4 text-brand-amber">Visit us</p>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li className="flex items-start gap-3">
                            <MapPin size={16} className="text-brand-amber mt-0.5 shrink-0" />
                            <span>
                                {HYDERABAD_ADDRESS.line1},<br />
                                {HYDERABAD_ADDRESS.line2},<br />
                                {HYDERABAD_ADDRESS.city}
                            </span>
                        </li>
                        <li className="flex items-center gap-3">
                            <Phone size={16} className="text-brand-amber" />
                            <a href={HYDERABAD_ADDRESS.whatsapp} target="_blank" rel="noreferrer" className="link-underline" data-testid="footer-phone">
                                {HYDERABAD_ADDRESS.phone}
                            </a>
                        </li>
                        <li className="flex items-center gap-3">
                            <Mail size={16} className="text-brand-amber" />
                            <a href={`mailto:${HYDERABAD_ADDRESS.email}`} className="link-underline" data-testid="footer-email">
                                {HYDERABAD_ADDRESS.email}
                            </a>
                        </li>
                    </ul>
                    <div className="flex gap-3 mt-6">
                        <span className="text-xs text-white/40">Social channels coming soon</span>
                    </div>
                </div>
            </div>

            <div className="container-page mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/55">
                <p>© {year} Vittaniti. Hyderabad, India. All rights reserved.</p>
                <p className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-amber" />
                    Crafted with quiet attention to detail.
                </p>
            </div>
        </footer>
    );
}
